/**
 * This file was directly extracted from ab-rp-growth-hub
 */
import { Deserializer } from 'jsonapi-serializer';
import { z } from 'zod';
import { request, useMutation, useQuery, useQueryClient } from '@applyboard/ui-utils';
import { AgencyEmployeeSchema, AgencySchema as BaseAgencySchema } from './common';

interface AgencyOptions {
  include?: string;
}

const AgencySchema = BaseAgencySchema.extend({
  agencyEmployee: AgencyEmployeeSchema.nullish(),
  logoAttachment: z
    .object({
      contentType: z.string(),
      fileName: z.string(),
      fileSize: z.number(),
      id: z.string(),
      urlOriginal: z.string(),
    })
    .nullish(),
  masterAgency: BaseAgencySchema.nullish(),
});

export type Agency = z.infer<typeof AgencySchema>;

interface UseAgency {
  agency: Agency | null;
  agencyError: Error | null;
  isFetchingAgency: boolean;
  isFetchAgencyError: boolean;
  isFetchAgencySuccessful: boolean;
  isLoadingAgency: boolean;
}

export function useAgency(
  id?: string | number,
  options: AgencyOptions = {},
  onError?: () => void,
): UseAgency {
  const { data, isSuccess, isLoading, isFetching, isError, error } = useQuery(
    [
      'agency',
      {
        id,
        pkg: 'ab-rp-growth-hub',
        ...options,
      },
    ],
    () => {
      const queryParams = [['include', options.include]];

      const queryString = new URLSearchParams(
        queryParams.filter(param => !!param[1]) as string[][],
      ).toString();

      return request(`/api/agencies/${id}?${queryString}`, {
        deserializer: new Deserializer({
          keyForAttribute: 'camelCase',
        }),
        schema: AgencySchema,
      });
    },
    {
      enabled: !!id,
      onError,
    },
  );

  return {
    agency: isSuccess ? data : null,
    agencyError: error,
    isFetchAgencyError: isError,
    isFetchAgencySuccessful: isSuccess,
    isFetchingAgency: isFetching,
    isLoadingAgency: isLoading,
  };
}

type PatchAgencyRequest = {
  agencyId: string;
  formData: object;
};

export function usePatchAgency() {
  const queryClient = useQueryClient();
  const { mutate, isLoading, isError } = useMutation(
    async ({ agencyId, formData }: PatchAgencyRequest) => {
      return request(`/api/agencies/${agencyId}`, {
        body: {
          data: {
            attributes: formData,
            type: 'agency',
          },
        },
        contentType: 'application/vnd.api+json',
        method: 'PATCH',
        schema: z.unknown(),
      });
    },
  );

  const patchAgency = (
    params: PatchAgencyRequest,
    onSuccess?: () => void,
    onError?: (error: unknown) => void,
  ) => {
    mutate(params, {
      onSuccess: () => {
        queryClient.invalidateQueries(['agency']);
        if (onSuccess) {
          onSuccess();
        }
      },
      onError,
    });
  };
  return {
    patchAgency,
    isPatchingAgency: isLoading,
    patchAgencyError: isError,
  };
}
